/**
 * SSE consumer for POST /api/projects/:projectId/turn-stream.
 *
 * Orval can't generate streaming clients, so we hand-roll this with
 * fetch + ReadableStream. Events fire via callbacks so React state
 * stays in the consumer's component.
 */

import { API_BASE } from "./custom-fetch";

export interface AiStreamingToolCall {
  readonly id: string;
  readonly name: string;
  readonly args?: unknown;
  readonly result?: string;
  readonly ok?: boolean;
}

export interface AiTurnCompleteEvent {
  readonly conversationId: string;
  readonly assistantText: string;
  readonly toolCallCount: number;
  readonly mutationCount: number;
  readonly iterations: number;
  readonly hitIterationCap: boolean;
  readonly durationMs: number;
  readonly finishReason: "stop" | "tool_use" | "length" | "error";
}

export interface AiStreamErrorEvent {
  readonly message: string;
  readonly code?: string;
}

export interface AiStreamHandlers {
  readonly onConversation?: (e: { id: string }) => void;
  readonly onIteration?: (e: { iteration: number }) => void;
  readonly onAssistantText?: (text: string) => void;
  readonly onToolCall?: (call: AiStreamingToolCall) => void;
  readonly onToolResult?: (call: AiStreamingToolCall) => void;
  readonly onTurnComplete?: (e: AiTurnCompleteEvent) => void;
  /**
   * Signature is `(messageOrEvent, code?)` for backwards compatibility:
   * old callers can keep treating the first arg as a string; new callers
   * can branch on `code` (e.g. "turn_aborted" → auto-retry on Sonnet).
   */
  readonly onError?: (message: string, code?: string) => void;
}

export interface StreamAiTurnArgs {
  readonly projectId: string;
  readonly body: {
    readonly conversationId?: string | null;
    readonly prompt: string;
    readonly modelOverride?: string;
    readonly imageUrls?: ReadonlyArray<string>;
  };
  readonly handlers?: AiStreamHandlers;
  readonly signal?: AbortSignal;
}

const EVENT_HANDLERS: Record<string, keyof AiStreamHandlers> = {
  conversation: "onConversation",
  iteration: "onIteration",
  assistant_text: "onAssistantText",
  tool_call: "onToolCall",
  tool_result: "onToolResult",
  turn_complete: "onTurnComplete",
  error: "onError",
};

export async function streamAiTurn(args: StreamAiTurnArgs): Promise<void> {
  const url = `${API_BASE}/api/projects/${encodeURIComponent(args.projectId)}/turn-stream`;
  await openSseStream(
    url,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(args.body),
    },
    args.handlers,
    args.signal,
  );
}

/**
 * Attach to an in-flight turn (or replay a recently finished one) for
 * this project. Returns true if the server had something to replay,
 * false if there's nothing to attach to (HTTP 204) — caller can then
 * start a fresh turn via {@link streamAiTurn}.
 */
export async function attachActiveAiTurn(args: {
  projectId: string;
  handlers?: AiStreamHandlers;
  signal?: AbortSignal;
}): Promise<boolean> {
  const url = `${API_BASE}/api/projects/${encodeURIComponent(args.projectId)}/turn-stream/active`;
  const res = await fetch(url, {
    method: "GET",
    credentials: "include",
    headers: { Accept: "text/event-stream" },
    ...(args.signal && { signal: args.signal }),
  });
  if (res.status === 204) return false;
  if (!res.ok || !res.body) {
    args.handlers?.onError?.(`Reattach failed: ${res.status}`);
    return false;
  }
  await consumeSseBody(res.body, args.handlers);
  return true;
}

async function openSseStream(
  url: string,
  init: { method: "GET" | "POST"; headers: Record<string, string>; body?: string },
  handlers: AiStreamHandlers | undefined,
  signal?: AbortSignal,
): Promise<void> {
  const res = await fetch(url, {
    method: init.method,
    credentials: "include",
    headers: { ...init.headers, Accept: "text/event-stream" },
    ...(init.body && { body: init.body }),
    ...(signal && { signal }),
  });
  if (!res.ok || !res.body) {
    const text = await res.text().catch(() => "");
    throw new Error(`streamAiTurn: ${res.status} ${text}`);
  }
  await consumeSseBody(res.body, handlers);
}

async function consumeSseBody(
  body: ReadableStream<Uint8Array>,
  handlers: AiStreamHandlers | undefined,
): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let currentEvent: string | null = null;

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    let nl: number;
    while ((nl = buffer.indexOf("\n")) >= 0) {
      const line = buffer.slice(0, nl).replace(/\r$/, "");
      buffer = buffer.slice(nl + 1);

      if (line === "") {
        currentEvent = null;
        continue;
      }
      if (line.startsWith(":")) continue; // SSE comment

      if (line.startsWith("event:")) {
        currentEvent = line.slice(6).trim();
        continue;
      }
      if (line.startsWith("data:")) {
        const data = line.slice(5).trim();
        if (!currentEvent) continue;
        const handlerKey = EVENT_HANDLERS[currentEvent];
        if (!handlerKey) continue;
        let parsed: unknown;
        try {
          parsed = JSON.parse(data);
        } catch {
          continue;
        }
        if (currentEvent === "assistant_text") {
          handlers?.onAssistantText?.((parsed as { text: string }).text);
        } else if (currentEvent === "error") {
          const e = parsed as { message: string; code?: string };
          handlers?.onError?.(e.message, e.code);
        } else {
          // typed pass-through for the rest
          const fn = handlers?.[handlerKey];
          if (typeof fn === "function") {
            (fn as (e: unknown) => void)(parsed);
          }
        }
      }
    }
  }
}
